/* =============================================================================
 * CONSENTIMIENTO · la unica puerta por la que puede entrar un pixel.
 * -----------------------------------------------------------------------------
 * REGLA DURA: mientras la respuesta no sea `granted`, **no sale ni una peticion
 * a un tercero**. No es «cargar apagado», es no cargar. El RGPD y la guia de la
 * AEPD no distinguen entre una cookie y una peticion que ya ha identificado al
 * visitante por IP y user-agent: cuando `fbevents.js` se descarga, el dano ya
 * esta hecho. Por eso este modulo no envuelve a un pixel dormido — decide si el
 * pixel llega a existir. Lo prueba `tracking.spec.ts`: cero peticiones a
 * facebook antes de pulsar aceptar.
 *
 * TRES ESTADOS, NO DOS. `unknown` (no ha contestado) no es lo mismo que `denied`
 * (ha dicho que no). Confundirlos hace una de estas dos cosas, las dos malas:
 * o el banner reaparece en cada carga a quien ya dijo que no, o se toma el
 * silencio por un si.
 * ========================================================================== */
import { CONSENT_MAX_AGE_DAYS, CONSENT_STORAGE_KEY } from './config';

export type ConsentState = 'unknown' | 'granted' | 'denied';

interface StoredConsent {
  readonly value: 'granted' | 'denied';
  /** Marca de tiempo en ms. Sirve para caducar el «si», no para identificar. */
  readonly at: number;
}

type Listener = (state: ConsentState) => void;

const listeners = new Set<Listener>();

/** `localStorage` revienta en modo privado de Safari y con cookies bloqueadas. */
function safeStorage(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Lee la respuesta guardada.
 *
 * Cualquier cosa que no sea un registro intacto y dentro de plazo se lee como
 * `unknown`, o sea «vuelve a preguntar». Un dato corrupto no puede interpretarse
 * como un permiso: en la duda, no hay permiso.
 */
export function readConsent(): ConsentState {
  const store = safeStorage();
  if (!store) return 'unknown';

  const raw = store.getItem(CONSENT_STORAGE_KEY);
  if (!raw) return 'unknown';

  try {
    const parsed = JSON.parse(raw) as Partial<StoredConsent>;
    if (parsed.value !== 'granted' && parsed.value !== 'denied') return 'unknown';
    if (typeof parsed.at !== 'number' || !Number.isFinite(parsed.at)) return 'unknown';

    const ageDays = (Date.now() - parsed.at) / 86_400_000;
    // Un reloj adelantado deja `ageDays` en negativo. Eso no es un consentimiento
    // fresco, es un dato en el que no se puede confiar: se vuelve a preguntar.
    if (ageDays < 0 || ageDays > CONSENT_MAX_AGE_DAYS) return 'unknown';

    return parsed.value;
  } catch {
    return 'unknown';
  }
}

/** Guarda la respuesta y avisa a quien escuche (el pixel, entre otros). */
export function writeConsent(value: 'granted' | 'denied'): void {
  const store = safeStorage();
  if (store) {
    const record: StoredConsent = { value, at: Date.now() };
    try {
      store.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
    } catch {
      // Sin sitio o sin permiso para escribir. La decision sigue valiendo para
      // ESTA sesion (los oyentes ya se enteran abajo); solo se pierde el
      // recuerdo, asi que se volvera a preguntar en la proxima visita. Preferible
      // a tumbar la pagina por no poder anotar algo.
    }
  }
  for (const listener of listeners) listener(value);
}

/** Se suscribe a los cambios. Devuelve la funcion para darse de baja. */
export function onConsentChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Solo para las pruebas: deja el modulo como recien cargado. */
export function resetConsentForTests(): void {
  listeners.clear();
  safeStorage()?.removeItem(CONSENT_STORAGE_KEY);
}
